import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './subscribe-v4.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconEnvelope = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <path d="m3 7 9 6 9-6"/>
  </svg>
);
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <path d="M16 2v4M8 2v4M3 10h18"/>
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
  </svg>
);
const IconSignal = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.7 5.7a9 9 0 0 0 0 12.6M18.3 5.7a9 9 0 0 1 0 12.6"/>
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
  </svg>
);
const IconMagazine = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H11v18H7.5A3.5 3.5 0 0 0 4 23V5.5ZM20 5.5A3.5 3.5 0 0 0 16.5 2H13v18h3.5A3.5 3.5 0 0 1 20 23V5.5Z"/>
  </svg>
);
const IconMic = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3"/>
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6"/>
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);

// ── Products ──────────────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    id: 'opportunity_unlocked',
    icon: IconEnvelope,
    name: 'Opportunity Unlocked',
    cadence: 'WEEKLY · DEFAULT',
    desc: 'Your flagship newsletter with top stories, analysis, and the articles shaping what comes next.',
    locked: true,
    iconVariant: 'teal',
    learnMoreUrl: '/newsletter',
  },
  {
    id: 'the_week_ahead',
    icon: IconCalendar,
    name: 'The Week Ahead',
    cadence: 'FRIDAYS',
    desc: 'Your executive briefing with key events, policy shifts, and what matters going into the weekend.',
    locked: false,
    iconVariant: 'gold',
    learnMoreUrl: '/the-week-ahead',
  },
  {
    id: 'first_round',
    icon: IconSignal,
    name: 'First Round',
    cadence: 'WEEKDAYS · MORNING',
    desc: 'A quick daily briefing with five developments you need to know each weekday morning.',
    locked: false,
    iconVariant: 'teal',
    learnMoreUrl: '/first-round',
  },
  {
    id: 'magazine',
    icon: IconMagazine,
    name: 'Opportunity Post Magazine',
    cadence: 'MONTHLY',
    desc: 'In-depth stories, exclusive interviews, and editorial features delivered once a month.',
    locked: false,
    iconVariant: 'gold',
    learnMoreUrl: '/magazine',
  },
  {
    id: 'round_table',
    icon: IconMic,
    name: 'Round Table Podcast',
    cadence: 'LAUNCH & EPISODE UPDATES',
    desc: 'Be the first to know about new episodes, topics, and behind-the-scenes updates.',
    locked: false,
    iconVariant: 'teal',
    learnMoreUrl: '/round-table',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function SubscribePage() {
  const [searchParams] = useSearchParams();
  const emailRef = useRef(null);

  const initSelected = () => {
    const qp = searchParams.get('product');
    const base = new Set(['opportunity_unlocked']);
    if (qp && PRODUCTS.some(p => p.id === qp)) base.add(qp);
    return base;
  };

  const [selectedProducts, setSelectedProducts] = useState(initSelected);
  const [email, setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const qp = searchParams.get('product');
    if (qp && PRODUCTS.some(p => p.id === qp)) {
      setSelectedProducts(prev => {
        if (prev.has(qp)) return prev;
        return new Set([...prev, qp]);
      });
    }
  }, [searchParams]); // eslint-disable-line

  const toggleProduct = (id, locked) => {
    if (locked) return;
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          products: Array.from(selectedProducts),
          source_page: 'subscribe_page',
          consent: true,
        }),
      });
      if (res.ok || res.status === 400) {
        setSuccess(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedList = PRODUCTS.filter(p => selectedProducts.has(p.id));

  return (
    <div className="sv4-wrap" data-testid="subscribe-page">
      <main className="sv4-main">
        <div className="sv4-shell">

          {/* ── Hero ──────────────────────────────────────────────────────── */}
          <section className="sv4-hero" aria-labelledby="sv4-page-title">

            {/* Left: copy */}
            <div className="sv4-hero-copy">
              <div className="sv4-eyebrow">Communication Station</div>
              <h1 className="sv4-h1" id="sv4-page-title">
                Stay informed,<br /><span>your way.</span>
              </h1>
              <p className="sv4-hero-lead">
                Timely insights, expert analysis, and the stories shaping careers, companies, and institutions—delivered directly to your inbox.
              </p>

              <div className="sv4-five-ways">Five ways to stay informed</div>

              {/* Trust badges */}
              <div className="sv4-trust-badges" aria-label="Subscription guarantees">
                <div className="sv4-trust-item">
                  <div className="sv4-trust-icon sv4-trust-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>
                  </div>
                  <div>
                    <div className="sv4-trust-line1">Always free</div>
                    <div className="sv4-trust-line2">No paywalls</div>
                  </div>
                </div>
                <div className="sv4-trust-item">
                  <div className="sv4-trust-icon sv4-trust-shield" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  </div>
                  <div>
                    <div className="sv4-trust-line1">Your privacy</div>
                    <div className="sv4-trust-line2">Never sold</div>
                  </div>
                </div>
                <div className="sv4-trust-item">
                  <div className="sv4-trust-icon sv4-trust-sliders" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="8" cy="6" r="2" fill="currentColor" stroke="none"/><circle cx="16" cy="12" r="2" fill="currentColor" stroke="none"/><circle cx="10" cy="18" r="2" fill="currentColor" stroke="none"/></svg>
                  </div>
                  <div>
                    <div className="sv4-trust-line1">Your choice</div>
                    <div className="sv4-trust-line2">Change anytime</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: illustration */}
            <div
              className="sv4-hero-art"
              role="img"
              aria-label="Professionals working in an editorial workspace with Opportunity Post"
            />

          </section>

          {/* ── Products ──────────────────────────────────────────────────── */}
          <section className="sv4-section" aria-label="Subscription products">

            <div className="sv4-product-grid" data-testid="subscribe-product-grid">
              {PRODUCTS.map(({ id, icon: Icon, name, cadence, desc, locked, iconVariant, learnMoreUrl }) => {
                const isSelected = selectedProducts.has(id);
                return (
                  <article
                    key={id}
                    className={`sv4-product-card${isSelected ? ' selected' : ''}`}
                    data-product={id}
                    tabIndex={0}
                    role="checkbox"
                    aria-checked={isSelected}
                    onClick={() => toggleProduct(id, locked)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleProduct(id, locked); } }}
                    data-testid={`subscribe-product-${id}`}
                  >
                    <span className={`sv4-product-check${isSelected ? ' checked' : ''}`} aria-hidden="true">
                      {isSelected ? '✓' : ''}
                    </span>
                    <div className={`sv4-product-icon icon-${iconVariant}`} aria-hidden="true">
                      <Icon />
                    </div>
                    <h3>{name}</h3>
                    <span className="sv4-cadence">{cadence}</span>
                    <p>{desc}</p>
                    <button
                      type="button"
                      className={`sv4-card-btn${locked ? ' sv4-card-btn--locked' : isSelected ? ' sv4-card-btn--added' : ''}`}
                      onClick={e => { e.stopPropagation(); toggleProduct(id, locked); }}
                      data-testid={`subscribe-product-btn-${id}`}
                    >
                      {locked
                        ? <><span>Included</span><span className="sv4-btn-check">✓</span></>
                        : isSelected ? 'Remove' : 'Add'
                      }
                    </button>
                    <Link
                      to={learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="sv4-learn-more"
                      data-testid={`subscribe-learn-more-${id}`}
                    >
                      Learn More →
                    </Link>
                  </article>
                );
              })}
            </div>

            {/* ── Email capture — always visible ────────────────────────── */}
            <div className="sv4-email-capture" data-testid="subscribe-email-capture">
              {success ? (
                <div className="sv4-capture-success" role="status" data-testid="subscribe-success">
                  <div className="sv4-capture-headline">You're subscribed.</div>
                  <p>Check your inbox for a confirmation. Welcome to Opportunity Post.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="sv4-capture-form" data-testid="subscribe-form">
                  <div className="sv4-capture-left">
                    <div className="sv4-capture-headline">Where should we send your selections?</div>
                    <div className="sv4-capture-sel">
                      {selectedList.map(p => (
                        <span key={p.id} className="sv4-sel-tag">{p.name}</span>
                      ))}
                    </div>
                  </div>
                  <div className="sv4-capture-right">
                    <div className="sv4-hero-email-row sv4-capture-email-row">
                      <span className="sv4-hero-email-icon" aria-hidden="true"><IconEnvelope /></span>
                      <input
                        ref={emailRef}
                        id="sv4-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter your email address"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        data-testid="subscribe-email"
                      />
                      <button
                        type="submit"
                        className="sv4-hero-submit"
                        disabled={loading}
                        data-testid="subscribe-submit"
                      >
                        {loading ? 'Saving…' : 'Subscribe now'}
                      </button>
                    </div>
                    <div className="sv4-capture-privacy">
                      <IconLock />
                      We respect your privacy. Unsubscribe anytime.
                    </div>
                  </div>
                </form>
              )}
            </div>

          </section>

        </div>
      </main>
    </div>
  );
}
